/**
 * Interceptor HTTP para manejar tokens de autenticación
 * Inyecta el token en las peticiones y maneja errores 401/403
 */
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { getAccessToken, getProfileToken } from '../utils/session-storage.util';
import { IAuthTokens } from '../models/auth.types';
import { SessionService } from '../services/session.service';
import { ToastService } from '../../services/toast.service';

// Flag para controlar si hay un refresh en curso
let isRefreshing = false;

// Subject para notificar cuando el token ha sido refrescado
const refreshTokenSubject: BehaviorSubject<IAuthTokens | null> = new BehaviorSubject<IAuthTokens | null>(null);

/**
 * Verifica si la petición es para autenticación (login/register)
 */
const isAuthRequest = (request: HttpRequest<unknown>): boolean => {
  const url = request.url.toLowerCase();
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/validate-register') ||
    url.includes('/auth/session') ||
    url.includes('/auth/refresh')
  );
}

/**
 * Verifica si la peticion es publica (no requiere token).
 * Las rutas del marketplace son publicas (solo lectura).
 * Las rutas de agent-chat envian token SI hay uno disponible,
 * pero no fallan si no hay (permiten uso anonimo).
 */
const isPublicRequest = (request: HttpRequest<unknown>): boolean => {
  const url = request.url.toLowerCase();
  return url.includes('/api/marketplace/');
}

/**
 * Rutas que envian token si existe, pero no deben fallar sin el.
 * No hacen refresh de token si el 401 falla.
 */
const isOptionalAuthRequest = (request: HttpRequest<unknown>): boolean => {
  const url = request.url.toLowerCase();
  return url.includes('/api/agent-chat/');
}

/**
 * Añade el token de autenticación y el usuarioId a la petición
 */
const addToken = (request: HttpRequest<unknown>, token: string, usuarioId?: string | null): HttpRequest<unknown> => {
  const headers: { [key: string]: string } = {
    Authorization: `Bearer ${token}`
  };
  
  // Agregar usuarioId en un header personalizado si está disponible
  if (usuarioId) {
    headers['X-User-Id'] = usuarioId;
  }
  
  return request.clone({
    setHeaders: headers
  });
}

/**
 * Interceptor HTTP funcional para Angular 17+
 */
export const AuthInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const sessionService = inject(SessionService);
  const toastService = inject(ToastService);

  // Función para manejar errores 401 (Unauthorized)
  const handle401Error = (req: HttpRequest<unknown>, nextHandler: HttpHandlerFn, originalError: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
    // Si no hay un refresh en curso, iniciar uno
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);
      
      return authService.refreshToken().pipe(
        switchMap((tokens: IAuthTokens) => {
          isRefreshing = false;
          refreshTokenSubject.next(tokens);
          
          // Reintentar la petición original con el nuevo token
          return nextHandler(addToken(req, tokens.accessToken));
        }),
        catchError((refreshError) => {
          isRefreshing = false;

          // Si falla el refresh, notificar al usuario y cerrar sesión
          toastService.warn('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', 'Sesión expirada');
          authService.logout();

          // Devolver el error HTTP original para que el componente reciba el mensaje del servidor
          return throwError(() => originalError);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    } else {
      // Si hay un refresh en curso, esperar a que termine
      return refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((tokens: IAuthTokens | null) => {
          // Reintentar la petición con el nuevo token
          if (tokens) {
            return nextHandler(addToken(req, tokens.accessToken));
          }
          return throwError(() => new Error('No se pudo obtener un token válido'));
        })
      );
    }
  };

  // No interceptar peticiones de autenticacion ni rutas publicas
  if (isAuthRequest(request) || isPublicRequest(request)) {
    return next(request);
  }

  // Rutas de auth opcional: enviar token si hay uno, pero nunca fallar por auth
  if (isOptionalAuthRequest(request)) {
    try {
      const profileToken = getProfileToken();
      const accessToken = getAccessToken();
      const token = profileToken || accessToken;
      if (token) {
        request = addToken(request, token);
      }
    } catch (e) {
      // Si falla obtener token, continuar sin el
    }
    return next(request);
  }
  
  // Si la petición ya tiene un header Authorization, no sobrescribirlo
  const hasAuthHeader = request.headers.has('Authorization');
  
  // Obtener usuarioId de la sesión
  const currentUser = sessionService.getCurrentUser();
  const usuarioId = currentUser?.profile?.user_id || currentUser?.id || null;
  
  // Solo añadir token si no existe ya un header Authorization
  if (!hasAuthHeader) {
    // Priorizar profileToken sobre accessToken
    const profileToken = getProfileToken();
    const accessToken = getAccessToken();
    const token = profileToken || accessToken;
    
    if (token) {
      request = addToken(request, token, usuarioId);
    } else if (usuarioId) {
      // Si no hay token pero hay usuarioId, agregarlo de todas formas
      request = request.clone({
        setHeaders: {
          'X-User-Id': usuarioId
        }
      });
    }
  } else if (usuarioId && !request.headers.has('X-User-Id')) {
    // Si ya tiene Authorization pero no tiene X-User-Id, agregarlo
    request = request.clone({
      setHeaders: {
        'X-User-Id': usuarioId
      }
    });
  }
  
  // Procesar la petición y manejar errores
  return next(request).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(request, next, error); // Pasar el error original
      }
      return throwError(() => error);
    })
  );
};
